import React from "react";
import {CheckCircleRounded} from "@mui/icons-material";
import type {ClContract, ClPortfolioKpis} from "../contractLinkageData";
import {CL_CURRENCY, CL_EXPIRING_DAYS} from "../contractLinkageData";
import type {ClPortfolioFilter, ClReviewTx} from "../contractLinkageUtils";
import {formatCompactCurrency, formatNumber} from "../contractLinkageUtils";

type Props = {
    contracts: ClContract[];
    rows: ClReviewTx[];
    portfolio: ClPortfolioKpis;
    activeFilter: ClPortfolioFilter | null;
    /** Toggles a filter on the contract list in the explorer below. */
    onToggleFilter: (key: ClPortfolioFilter) => void;
};

const TONES: Record<ClPortfolioFilter, string> = {
    active: 'reviewed',
    expiring: 'unmatched',
    expired: 'low',
    attention: 'pending',
};

const LABELS: Record<ClPortfolioFilter, string> = {
    active: 'Active contracts',
    expiring: `Expiring ≤ ${CL_EXPIRING_DAYS} days`,
    expired: 'Expired',
    attention: 'Links to review',
};

export const ExplorerStatusCards: React.FC<Props> = ({contracts, rows, portfolio, activeFilter, onToggleFilter}) => {
    const cards = React.useMemo(() => {
        const pendingByContract = new Map<string, number>();
        rows.forEach(tx => {
            if (tx.status !== 'pending' || !tx.effectiveContractId) return;
            pendingByContract.set(tx.effectiveContractId, (pendingByContract.get(tx.effectiveContractId) ?? 0) + 1);
        });
        const pendingLinks = rows.filter(tx => tx.status === 'pending').length;
        const activeValue = contracts
            .filter(c => c.status === 'active')
            .reduce((s, c) => s + c.contractedValue, 0);
        return [
            {
                key: 'active' as const,
                count: portfolio.active,
                hint: `${formatCompactCurrency(activeValue, CL_CURRENCY)} contracted value`,
            },
            {
                key: 'expiring' as const,
                count: portfolio.expiring,
                hint: 'Renewals to prepare — check terms & utilisation',
            },
            {
                key: 'expired' as const,
                count: portfolio.expired,
                hint: portfolio.expiredTrailingSpend > 0
                    ? `${formatCompactCurrency(portfolio.expiredTrailingSpend, CL_CURRENCY)} spend after expiry`
                    : 'No spend after expiry',
            },
            {
                key: 'attention' as const,
                count: pendingLinks,
                hint: `AI links awaiting a decision on ${formatNumber(pendingByContract.size)} contracts`,
            },
        ];
    }, [contracts, rows, portfolio]);

    return (
        <div className="cl-kpi-cards">
            {cards.map(({key, count, hint}) => {
                const active = activeFilter === key;
                return (
                    <button
                        key={key}
                        type="button"
                        className={`cl-kpi-card tone-${TONES[key]}${active ? ' is-active' : ''}`}
                        aria-pressed={active}
                        onClick={() => onToggleFilter(key)}>
                        <span className="cl-kpi-head">
                            <span className="cl-kpi-dot"/>
                            {LABELS[key]}
                            {active
                                ? <CheckCircleRounded className="cl-kpi-check" fontSize="inherit"/>
                                : <span className="cl-kpi-filterhint">Filter</span>}
                        </span>
                        <span className="cl-kpi-value">{formatNumber(count)}</span>
                        <span className="cl-kpi-hint">{hint}</span>
                    </button>
                );
            })}
        </div>
    );
};
